/**
 * Plain-English explainers for the YouTube (Agent 1) pages, mirroring the
 * salience explainers so both modules read as one portal. Short leads written
 * for a casual user who doesn't know what "ingest", "classify" or "digest
 * pipeline" mean, with the deeper detail tucked into collapsible callouts.
 *
 * Reuses the shared InfoCallout primitive from the salience folder (it's a
 * generic terminal-design component, not salience-specific).
 *
 * House style: NO em-dashes in any visible copy (user preference).
 */
import { Link } from "react-router-dom";
import { InfoCallout } from "../salience/InfoCallout";

type Tone = "dark" | "light";

/** Small "how the whole thing works" link, tone-aware. */
export function MethodsLink({ tone = "dark" }: { tone?: Tone }) {
  const cls =
    tone === "light"
      ? "text-brand-indigo hover:underline"
      : "text-[#7da6ff] hover:underline";
  return (
    <Link to="/youtube/methods" className={`text-[11px] font-medium ${cls}`}>
      How this works, in full →
    </Link>
  );
}

/** The collect → tag → summarise pipeline, in plain English. */
export function HowItWorksCallout({ tone = "light" }: { tone?: Tone }) {
  return (
    <InfoCallout label="How this works" tone={tone}>
      <p>
        Everything here is built automatically from the comments on Gary's
        YouTube videos, in three steps:
      </p>
      <ul className="space-y-1.5">
        <li>
          <span className="font-semibold">Collect:</span> we regularly pull in
          the latest comments from every video and keep them up to date.
        </li>
        <li>
          <span className="font-semibold">Tag:</span> an AI reads each comment and
          labels it. The tone toward Gary, what the person is doing (a question,
          pushback, a story, a request), whether it needs a safety flag, and which
          topic it touches.
        </li>
        <li>
          <span className="font-semibold">Summarise:</span> on a regular schedule,
          an AI writes a short summary (a "digest") for each video, so the team
          can read one page instead of scrolling thousands of comments.
        </li>
      </ul>
      <p>
        There is a built-in delay: a brand-new comment can take a day or two to be
        tagged, and summaries are written on a schedule rather than instantly.
      </p>
      <MethodsLink tone={tone} />
    </InfoCallout>
  );
}

/** Sentiment = tone toward Gary, an AI read not a fact. */
export function SentimentCallout({ tone = "dark" }: { tone?: Tone }) {
  return (
    <InfoCallout label="How this works" tone={tone}>
      <p>
        Sentiment is the overall tone of a comment toward Gary and his content:
        supportive, unsupportive, or neutral. It is the AI's best read of the
        mood, not a verified fact, so treat the split as a broad indication rather
        than a precise measure.
      </p>
    </InfoCallout>
  );
}

/** General tags = what the commenter is doing; counts can overlap. */
export function IntentCallout({ tone = "dark" }: { tone?: Tone }) {
  return (
    <InfoCallout label="How this works" tone={tone}>
      <p>
        This groups comments by what the person is actually doing: asking a
        question, pushing back on a point, sharing a personal story, or requesting
        content. A single comment can do more than one of these, so the counts can
        add up to more than the number of comments.
      </p>
    </InfoCallout>
  );
}

/** Topics = curated picklist; most comments get none. */
export function TopicsCallout({ tone = "dark" }: { tone?: Tone }) {
  return (
    <InfoCallout label="How this works" tone={tone}>
      <p>
        Topics are a short, curated list of themes we tag comments against. Most
        comments get no topic, only the ones clearly about a tracked theme, so
        this shows what the audience is specifically talking about rather than a
        full breakdown of every comment.
      </p>
    </InfoCallout>
  );
}

/** What a digest is (used on the per-video reading page). */
export function DigestCallout({ tone = "light" }: { tone?: Tone }) {
  return (
    <InfoCallout label="What is a digest?" tone={tone}>
      <p>
        A digest is a short, written summary of what a video's comments are
        saying: the questions, the pushback, the stories, and anything worth a
        closer look. An AI writes it from the tagged comments so Gary and the team
        can read one page instead of thousands of comments. It refreshes
        automatically as more comments come in.
      </p>
      <MethodsLink tone={tone} />
    </InfoCallout>
  );
}

/**
 * A calm, honest prototype note. `variant="inline"` is a one-liner for the top
 * of a page; `variant="full"` is a short paragraph for the methods page.
 */
export function PrototypeNote({
  tone = "light",
  variant = "inline",
}: {
  tone?: Tone;
  variant?: "inline" | "full";
}) {
  const base = tone === "light" ? "text-ink-muted" : "text-terminal-ink-muted";

  if (variant === "inline") {
    return (
      <p className={`text-[11px] leading-relaxed ${base}`}>
        <span className="font-semibold">Early prototype.</span> Comments are
        collected and tagged automatically with very little human checking, so
        expect some mis-tags, gaps, and errors. Read it for the overall picture,
        not exact numbers.
      </p>
    );
  }

  return (
    <div className={`text-sm leading-relaxed ${base}`}>
      <p>
        This is an early prototype, built to show what the approach can do. The
        comments are collected from YouTube and tagged by an AI with very little
        human checking so far, so expect some mis-tags, gaps, and the occasional
        wrong call. Treat everything here as directional: read the overall shape
        and the individual comments rather than leaning on any single number.
      </p>
    </div>
  );
}
